import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { PhoneInput } from './ui/phone-input';
import { Loader2, User, Briefcase, Edit, CreditCard, Camera, X, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AddEmployeeModal = ({ open, onClose, onEmployeeAdded, departments, employee = null }) => {
  const isEditMode = !!employee;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [jobTitles, setJobTitles] = useState([]);
  const [officeCurrency, setOfficeCurrency] = useState('TZS');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const { getAuthHeaders, currentOffice, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    national_id: '',
    tax_number: '',
    pension_number: '',
    
    // Contact Information
    phone: '',
    email: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    address: '',
    city: '',
    po_box: '',
    
    // Employment Details
    employee_id: '',
    department: '',
    position: '',
    employment_type: 'Full-time',
    employment_status: 'Active',
    hire_date: '',
    reporting_manager_id: '',
    work_location: 'Office',
    
    // Compensation
    gross_salary: '',
    salary_currency: 'TZS',
    
    // Bank Details
    bank_name: '',
    account_name: '',
    account_number: ''
  });

  const EMERGENCY_CONTACT_RELATIONSHIPS = [
    'Father', 'Mother', 'Brother', 'Sister', 'Uncle', 'Aunt', 
    'Husband', 'Wife', 'Son', 'Daughter', 'Friend'
  ];

  // Fetch job titles, employee ID, and set office currency when modal opens
  useEffect(() => {
    if (open) {
      fetchJobTitles();
      setError('');
      setActiveTab('personal');
      
      if (isEditMode && employee) {
        // Populate form with employee data for editing
        setFormData({
          first_name: employee.first_name || '',
          last_name: employee.last_name || '',
          middle_name: employee.middle_name || '',
          date_of_birth: employee.date_of_birth || '',
          gender: employee.gender || '',
          nationality: employee.nationality || '',
          national_id: employee.national_id || '',
          tax_number: employee.tax_number || '',
          pension_number: employee.pension_number || '',
          phone: employee.contact?.phone || '',
          email: employee.contact?.email || '',
          emergency_contact_name: employee.contact?.emergency_contact_name || '',
          emergency_contact_phone: employee.contact?.emergency_contact_phone || '',
          emergency_contact_relationship: employee.contact?.emergency_contact_relationship || '',
          address: employee.contact?.address || '',
          city: employee.contact?.city || '',
          po_box: employee.contact?.postal_code || '',
          employee_id: employee.employment?.employee_id || '',
          department: employee.employment?.department || '',
          position: employee.employment?.job_title || '',
          employment_type: employee.employment?.employment_type || 'Full-time',
          employment_status: employee.employment?.employment_status || 'Active',
          hire_date: employee.employment?.hire_date || '',
          reporting_manager_id: employee.employment?.reporting_manager_id || '',
          work_location: employee.employment?.work_location || 'Office',
          gross_salary: '',
          salary_currency: employee.employment?.salary_currency || currentOffice?.currency || 'TZS',
          bank_name: employee.bank_details?.bank_name || '',
          account_name: employee.bank_details?.account_name || '',
          account_number: employee.bank_details?.account_number || ''
        });
        // Set existing profile picture
        if (employee.profile_picture) {
          setProfilePicturePreview(employee.profile_picture);
        } else {
          setProfilePicturePreview(null);
        }
        setProfilePicture(null);
      } else {
        // Reset form for adding new employee
        resetForm();
        fetchNextEmployeeId();
        setProfilePicture(null);
        setProfilePicturePreview(null);
      }
      
      // Set default currency from currentOffice
      if (currentOffice?.currency && !isEditMode) {
        setOfficeCurrency(currentOffice.currency);
        setFormData(prev => ({ ...prev, salary_currency: currentOffice.currency }));
      }
    }
  }, [open, currentOffice, employee, isEditMode]);

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', middle_name: '', date_of_birth: '',
      gender: '', nationality: '', national_id: '', tax_number: '', pension_number: '',
      phone: '', email: '',
      emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
      address: '', city: '', po_box: '', employee_id: '', department: '',
      position: '', employment_type: 'Full-time', employment_status: 'Active',
      hire_date: '', reporting_manager_id: '', work_location: 'Office',
      gross_salary: '', salary_currency: 'TZS',
      bank_name: '', account_name: '', account_number: ''
    });
  };

  const fetchNextEmployeeId = async () => {
    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id || user?.roles?.[0]?.office_id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      
      const response = await fetch(`${API_URL}/api/employees/next-employee-id`, { headers });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, employee_id: data.next_employee_id }));
      }
    } catch (error) {
      console.error('Error fetching next employee ID:', error);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const headers = getAuthHeaders();
      const officeId = currentOffice?.id || user?.roles?.[0]?.office_id;
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }
      
      const response = await fetch(`${API_URL}/api/system-config/job-titles`, { headers });
      if (response.ok) {
        const data = await response.json();
        setJobTitles(data);
      }
    } catch (error) {
      console.error('Error fetching job titles:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const officeId = currentOffice?.id || user?.roles?.[0]?.office_id;
      
      const employeeData = {
        office_id: officeId,
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name || null,
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender || null,
        nationality: formData.nationality || null,
        national_id: formData.national_id || null,
        tax_number: formData.tax_number || null,
        pension_number: formData.pension_number || null,
        
        contact: {
          phone: formData.phone,
          email: formData.email || null,
          emergency_contact_name: formData.emergency_contact_name || null,
          emergency_contact_phone: formData.emergency_contact_phone || null,
          emergency_contact_relationship: formData.emergency_contact_relationship || null,
          address: formData.address || null,
          city: formData.city || null,
          postal_code: formData.po_box || null
        },
        
        employment: {
          employee_id: formData.employee_id,
          department: formData.department,
          job_title: formData.position,
          employment_type: formData.employment_type,
          employment_status: formData.employment_status,
          hire_date: formData.hire_date,
          reporting_manager_id: formData.reporting_manager_id || null,
          work_location: formData.work_location,
          special_roles: []
        },
        
        bank_details: {
          bank_name: formData.bank_name || null,
          account_name: formData.account_name || null,
          account_number: formData.account_number || null
        },
        
        skills: [],
        education: [],
        certifications: [],
        create_user_account: !isEditMode  // Only create user account when adding new employee
      };

      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      if (officeId) {
        headers['X-Office-ID'] = officeId;
      }

      const url = isEditMode 
        ? `${API_URL}/api/employees/${employee.id}`
        : `${API_URL}/api/employees/`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(employeeData)
      });

      if (response.ok) {
        const responseData = await response.json();
        const employeeId = responseData.id || employee?.id;
        
        // Upload profile picture if selected
        if (profilePicture && employeeId) {
          try {
            const pictureHeaders = getAuthHeaders();
            pictureHeaders['Content-Type'] = 'application/json';
            if (officeId) {
              pictureHeaders['X-Office-ID'] = officeId;
            }
            
            await fetch(`${API_URL}/api/employees/${employeeId}/profile-picture`, {
              method: 'POST',
              headers: pictureHeaders,
              body: JSON.stringify({ image_data: profilePicture })
            });
          } catch (pictureError) {
            console.error('Error uploading profile picture:', pictureError);
          }
        }
        
        onEmployeeAdded();
        onClose();
        resetForm();
        setProfilePicture(null);
        setProfilePicturePreview(null);
        setActiveTab('personal');
      } else {
        const errorData = await response.json();
        // Provide clear error messages
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            setError(errorData.detail);
          } else if (Array.isArray(errorData.detail)) {
            const messages = errorData.detail.map(err => 
              `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
            ).join(', ');
            setError(messages);
          } else {
            setError(`Failed to ${isEditMode ? 'update' : 'create'} employee. Please check all required fields.`);
          }
        } else {
          setError(`Failed to ${isEditMode ? 'update' : 'create'} employee. Please try again.`);
        }
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.first_name && formData.last_name && formData.phone && 
           formData.employee_id && formData.department && formData.position && 
           formData.hire_date;
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      // Read file and convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result);
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={isEditMode ? "edit-employee-modal" : "add-employee-modal"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode ? <Edit className="h-5 w-5" /> : <User className="h-5 w-5" />}
            {isEditMode ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? `Update information for ${employee?.first_name} ${employee?.last_name}`
              : 'Fill in the employee information across the different tabs'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isEditMode && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900">
              <div className="flex-1 text-sm">
                <div className="font-medium text-blue-900 dark:text-blue-100">
                  Onboarding a new teammate?
                </div>
                <div className="text-xs text-blue-800/80 dark:text-blue-200/80">
                  Share this Excel form for them to fill in — use their answers to complete the fields below.
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/employees/onboarding-template`, {
                      headers: getAuthHeaders(),
                    });
                    if (!res.ok) {
                      setError('Failed to download onboarding template');
                      return;
                    }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'employee_onboarding_template.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  } catch {
                    setError('Network error downloading template');
                  }
                }}
                className="gap-2 shrink-0 bg-white"
                data-testid="download-onboarding-template"
              >
                <Download className="h-4 w-4" />
                Download Onboarding Form
              </Button>
            </div>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="personal" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Personal
              </TabsTrigger>
              <TabsTrigger value="contact">Contact</TabsTrigger>
              <TabsTrigger value="employment" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Employment
              </TabsTrigger>
              <TabsTrigger value="financial" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Financial
              </TabsTrigger>
            </TabsList>

            <TabsContent value="personal" className="space-y-4">
              {/* Profile Picture Section */}
              <div className="flex items-center gap-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="relative">
                  {profilePicturePreview ? (
                    <div className="relative">
                      <img 
                        src={profilePicturePreview} 
                        alt="Profile preview" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeProfilePicture}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                        data-testid="remove-profile-picture-btn"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <User className="h-10 w-10 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium">Profile Picture</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Upload a photo (max 5MB). Image will be optimized automatically.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                    id="profile-picture-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-profile-picture-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    {profilePicturePreview ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    required
                    data-testid="last-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="middle_name">Middle Name</Label>
                  <Input
                    id="middle_name"
                    value={formData.middle_name}
                    onChange={(e) => handleInputChange('middle_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender || undefined} onValueChange={(value) => handleInputChange('gender', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="nationality">Nationality</Label>
                  <Input
                    id="nationality"
                    value={formData.nationality}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="national_id">National ID</Label>
                  <Input
                    id="national_id"
                    value={formData.national_id}
                    onChange={(e) => handleInputChange('national_id', e.target.value)}
                    placeholder="e.g., 19900101-12345-00001-23"
                  />
                </div>
                <div>
                  <Label htmlFor="tax_number">Tax Number (TIN)</Label>
                  <Input
                    id="tax_number"
                    value={formData.tax_number}
                    onChange={(e) => handleInputChange('tax_number', e.target.value)}
                    placeholder="e.g., 123-456-789"
                    data-testid="tax-number-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pension_number">Pension Number (NSSF)</Label>
                  <Input
                    id="pension_number"
                    value={formData.pension_number}
                    onChange={(e) => handleInputChange('pension_number', e.target.value)}
                    placeholder="e.g., NSSF123456"
                    data-testid="pension-number-input"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contact" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <PhoneInput
                    id="phone"
                    value={formData.phone}
                    onChange={(value) => handleInputChange('phone', value || '')}
                    placeholder="712345678"
                    defaultCountry="TZ"
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact_name">Emergency Contact Name</Label>
                  <Input
                    id="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact_phone">Emergency Contact Phone</Label>
                  <PhoneInput
                    id="emergency_contact_phone"
                    value={formData.emergency_contact_phone}
                    onChange={(value) => handleInputChange('emergency_contact_phone', value || '')}
                    placeholder="712345678"
                    defaultCountry="TZ"
                    data-testid="emergency-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact_relationship">Relationship</Label>
                  <Select
                    value={formData.emergency_contact_relationship}
                    onValueChange={(value) => handleInputChange('emergency_contact_relationship', value)}
                  >
                    <SelectTrigger id="emergency_contact_relationship">
                      <SelectValue placeholder="Select relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMERGENCY_CONTACT_RELATIONSHIPS.map((rel) => (
                        <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="po_box">Post Office Box</Label>
                  <Input
                    id="po_box"
                    value={formData.po_box}
                    onChange={(e) => handleInputChange('po_box', e.target.value)}
                    placeholder="e.g., P.O. Box 12345"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="employment" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employee_id">Employee ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="employee_id"
                      value={formData.employee_id}
                      readOnly
                      className="bg-gray-50 font-mono"
                      data-testid="employee-id-input"
                    />
                    <span className="text-xs text-gray-500 self-center whitespace-nowrap">Auto-generated</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="department">Department *</Label>
                  <Select value={formData.department || undefined} onValueChange={(value) => handleInputChange('department', value)}>
                    <SelectTrigger data-testid="department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.name}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="position">Position *</Label>
                  <Select value={formData.position || undefined} onValueChange={(value) => handleInputChange('position', value)}>
                    <SelectTrigger data-testid="position-select">
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobTitles.length > 0 ? (
                        jobTitles.map((jt) => (
                          <SelectItem key={jt.id} value={jt.title}>
                            {jt.title}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Manager">Manager</SelectItem>
                          <SelectItem value="Supervisor">Supervisor</SelectItem>
                          <SelectItem value="Staff">Staff</SelectItem>
                          <SelectItem value="Intern">Intern</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="employment_type">Employment Type</Label>
                  <Select value={formData.employment_type} onValueChange={(value) => handleInputChange('employment_type', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="hire_date">Hire Date *</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => handleInputChange('hire_date', e.target.value)}
                    required
                    data-testid="hire-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="work_location">Work Location</Label>
                  <Select value={formData.work_location} onValueChange={(value) => handleInputChange('work_location', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Office">Office</SelectItem>
                      <SelectItem value="Remote">Remote</SelectItem>
                      <SelectItem value="Hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Financial Tab */}
            <TabsContent value="financial" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    placeholder="e.g., CRDB Bank, NMB Bank"
                    data-testid="bank-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="account_name">Account Name</Label>
                  <Input
                    id="account_name"
                    value={formData.account_name}
                    onChange={(e) => handleInputChange('account_name', e.target.value)}
                    placeholder="Account holder name"
                    data-testid="account-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => handleInputChange('account_number', e.target.value)}
                    placeholder="Bank account number"
                    data-testid="account-number-input"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-between pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !isFormValid()}
              data-testid="submit-employee"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Employee' : 'Create Employee'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeModal;
